// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { Fragment, useState, type ReactNode } from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { ChevronDown, ChevronRight } from 'lucide-react'

export interface GroupedLine { key: string; label: ReactNode; sub?: ReactNode; cells: ReactNode[] }
export interface LineGroup { key: string; label: ReactNode; cells: ReactNode[]; lines: GroupedLine[]; defaultOpen?: boolean }

/**
 * D-UI-3 — a breakdown: groups with subtotal rows that expand to their lines. Value columns are
 * right-aligned and tabular. Use for "where did this total come from", not for paged data lists (DataGrid).
 */
export default function GroupedLines({ columns, groups, total }: {
  columns: string[]; groups: LineGroup[]; total?: { label: ReactNode; cells: ReactNode[] }
}) {
  const [open, setOpen] = useState<Record<string, boolean>>(() => Object.fromEntries(groups.map(g => [g.key, !!g.defaultOpen])))
  const num = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontSize: 11, color: 'text.secondary', textTransform: 'uppercase' }} />
          {columns.map(c => <TableCell key={c} align="right" sx={{ fontSize: 11, color: 'text.secondary', textTransform: 'uppercase' }}>{c}</TableCell>)}
        </TableRow>
      </TableHead>
      <TableBody>
        {groups.map(g => {
          const isOpen = !!open[g.key]
          return (
            <Fragment key={g.key}>
              <TableRow hover sx={{ bgcolor: 'action.hover', cursor: g.lines.length ? 'pointer' : undefined }}
                onClick={() => g.lines.length && setOpen(o => ({ ...o, [g.key]: !isOpen }))}>
                <TableCell sx={{ fontWeight: 700 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton size="small" aria-label={`${isOpen ? 'Collapse' : 'Expand'} group`} aria-expanded={isOpen}
                      disabled={!g.lines.length} sx={{ p: 0.25 }}
                      onClick={e => { e.stopPropagation(); setOpen(o => ({ ...o, [g.key]: !isOpen })) }}>
                      {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                    </IconButton>
                    {g.label}
                  </Box>
                </TableCell>
                {g.cells.map((c, i) => <TableCell key={i} align="right" sx={{ ...num, fontWeight: 700 }}>{c}</TableCell>)}
              </TableRow>
              {isOpen && g.lines.map(l => (
                <TableRow key={`${g.key}:${l.key}`}>
                  <TableCell sx={{ pl: 5.5 }}>
                    {l.label}
                    {l.sub && <Box sx={{ fontSize: 11.5, color: 'text.secondary' }}>{l.sub}</Box>}
                  </TableCell>
                  {l.cells.map((c, i) => <TableCell key={i} align="right" sx={num}>{c}</TableCell>)}
                </TableRow>
              ))}
            </Fragment>
          )
        })}
        {total && (
          <TableRow>
            <TableCell sx={{ fontWeight: 800, borderTop: 2, borderColor: 'divider' }}>{total.label}</TableCell>
            {total.cells.map((c, i) => <TableCell key={i} align="right" sx={{ ...num, fontWeight: 800, borderTop: 2, borderColor: 'divider' }}>{c}</TableCell>)}
          </TableRow>
        )}
      </TableBody>
    </Table>
  )
}
